import { reactive } from 'vue'
import { forEach } from 'lodash'
import { useCookies } from '@vueuse/integrations/useCookies'
import axios from '@/services/api/wrapper/axiosInstance'
import ENV from '@/constants/env'
import { CACHE_KEY } from '@/constants/query'
import { ApiClient } from '@/services/api/client'
import { AxiosHttpWrapper } from '@/services/api/wrapper/HttpRequestWrapper'
import type { Api, CancelKey, Options } from '@/types/composables/api'
import { COOKIE_OPTIONS } from '@/constants/cookies'
import type { OpenAPIConfig } from '@/services/api/client/core/OpenAPI'
import type { AxiosError, AxiosResponse } from 'axios'
import { TIME } from '@/constants/time'

const apiHost = ENV.apiHost

const useApi = (config?: Partial<OpenAPIConfig>): Api => {
    const api = new ApiClient(
        {
            BASE: apiHost,
            ...config
        },
        AxiosHttpWrapper
    )

    const controllers = reactive<Record<string, AbortController>>({})

    const cookies = useCookies()

    const request = (method: string, url: string, data?: unknown, options?: Options, legacy = false) => {
        if (options?.cancel) {
            controllers[options.cancel] = new AbortController()
        }

        return axios({
            method,
            url: `${legacy ? window.origin : apiHost}${url}`,
            headers: {
                ...(options?.headers ? options.headers : {})
            },
            ...(data ? { data } : {}),
            ...(options?.cancel
                ? {
                      signal: controllers[options.cancel].signal
                  }
                : {}),
            ...(options?.config ? options.config : {}),
            // let Axios automagically figure out content type for legacy endpoints
            ...(legacy && !options?.headers?.['Content-Type']
                ? {
                      transformRequest: [
                          (data, headers) => {
                              headers['Content-Type'] = null
                              return data
                          }
                      ]
                  }
                : {})
        })
            .catch((error: AxiosError) => {
                switch (error?.message) {
                    case `Request aborted`:
                    case `canceled`:
                        return Promise.resolve({})
                }

                return Promise.reject(error)
            })
            .then((response: AxiosResponse | Record<string, never>) => Promise.resolve(response.data))
    }

    const cancelCall = (cancelKey: CancelKey) => {
        if (controllers[cancelKey]) {
            controllers[cancelKey].abort()
        }
    }

    /**
     * Converts a valid access token into a session_key.
     *
     * There are two `/auth/sessionkey` endpoints and they point in opposite directions. This
     * authenticated GET is the preferred one for new work. Its counterpart, the unauthenticated
     * `POST /auth/sessionkey` in `services/api/wrapper/axiosInstance.ts`, trades a session_key for
     * an access token instead and is legacy: session_key is a core concept rather than an official
     * Laravel credential, and the direction of travel is away from treating it as one. That one
     * survives only for Command Center impersonation and is expected to be removed.
     */
    const refreshSessionKey = async () => {
        const response = await call(`GET`, `/auth/sessionkey`)

        // An aborted or cancelled request resolves to an empty object, so the response carries no
        // session. Writing that through would blank the cookie and lose a working session key,
        // and the server has already discarded the old row by this point, making it unrecoverable.
        if (!response?.session) {
            return
        }

        // Matches the server, which sets the session row a month out. Session keys used to last
        // roughly two years; a month is a deliberate reduction, held at that length because a
        // shorter window surfaced timing issues.
        cookies.set(`session_key`, response.session, {
            ...COOKIE_OPTIONS,
            expires: new Date(Date.now() + TIME.Day(30))
        })
    }

    const logout = () => {
        // Cancel pending requests
        forEach(controllers, cancelKey => {
            cancelKey.abort()
        })

        // Close Echo Channels
        window.echo?.leaveAllChannels()
        window.messagingEcho?.leaveAllChannels()

        // Get Cookies before deleting
        const accessToken = cookies.get(`access_token`)
        const sessionKey = cookies.get(`session_key`)

        // Delete Cookies
        cookies.remove(`access_token`, COOKIE_OPTIONS)
        cookies.remove(`session_key`, COOKIE_OPTIONS)
        cookies.remove(`support_user_r`, COOKIE_OPTIONS)
        cookies.remove(`is_support_user_id_r`, COOKIE_OPTIONS)

        // TODO: Exit Helm Impersonation (we have 2 sessions to logout)

        // Clear Cache
        localStorage.removeItem(CACHE_KEY)

        if (!accessToken) {
            return Promise.resolve()
        }

        return axios({
            method: `POST`,
            url: `${ENV.apiHost}/auth/logout`,
            headers: {
                Authorization: `Bearer ${accessToken}`
            },
            data: {
                requestBody: {
                    session_key: sessionKey
                }
            }
        })
    }

    const call = (method: string, url: string, payload?: unknown, options?: Options) => {
        if (options?.cancel && controllers[options.cancel]) {
            controllers[options.cancel].abort()
        }

        return request(method, url, payload, options).finally(() => {
            if (options?.cancel && controllers[options?.cancel]) {
                delete controllers[options?.cancel]
            }
        })
    }

    const callLegacy = (method: string, url: string, payload?: unknown, options?: Options) => {
        if (options?.cancel && controllers[options.cancel]) {
            controllers[options.cancel].abort()
        }

        return request(method, url, payload, options, true).finally(() => {
            if (options?.cancel && controllers[options?.cancel]) {
                delete controllers[options?.cancel]
            }
        })
    }

    /**
     * Bind methods to window to allow direct access from Zephyr
     */
    const init = () => {
        window.apiCall = call
        window.apiCancel = cancelCall
        window.apiReady = true
    }

    return {
        init,
        refreshSessionKey,
        logout,
        call,
        callLegacy,
        cancelCall,
        api
    }
}

export default useApi
